"use client"

/**
 * TurnstileGate — drop-in wrapper that:
 *   1. Renders the Cloudflare Turnstile widget.
 *   2. Provides a single UNCONTROLLED hidden input the widget writes its
 *      token into directly (Cloudflare's default behavior — when the widget
 *      has a sibling hidden input with name="cf-turnstile-response", it
 *      populates it on every successful challenge). The form's FormData
 *      always reads a fresh value without depending on React state being
 *      flushed between the widget callback and the form submit.
 *   3. Tracks a separate `verified` boolean via React state — used only to
 *      flip the submit button's `disabled` attribute. We do NOT use the
 *      token value itself in state, only the fact that a token was issued.
 *   4. Exposes `verified` + `errored` via a render prop.
 *
 * Why uncontrolled hidden input + React state for the boolean:
 *   - The token must be on the form's serialized payload at the instant the
 *     user clicks submit. A React-controlled `<input value={token}>` can
 *     lag behind the widget callback (React 18 batches state updates), and
 *     `FormData(form)` reads from the DOM — a stale React render leaves the
 *     DOM value empty. Cloudflare writing directly into an uncontrolled
 *     input eliminates that race entirely.
 *   - The `verified` boolean is only used for the button's render-prop, so
 *     a single render lag is harmless (the button enables a tick after the
 *     widget, which matches user expectation).
 *
 * Usage:
 *   <TurnstileGate>
 *     {({ verified }) => (
 *       <button type="submit" disabled={!verified}>SUBMIT</button>
 *     )}
 *   </TurnstileGate>
 */

import { useState, type ReactNode } from "react"
import TurnstileWidget from "./Turnstile"

type RenderProps = {
  /** True when the visitor has a fresh token (button can be enabled). */
  verified: boolean
  /** True after a hard error or missing-site-key fallback. */
  errored: boolean
}

type Props = {
  children: (props: RenderProps) => ReactNode
  /** Optional className for the widget container. */
  className?: string
}

export default function TurnstileGate({ children, className }: Props) {
  const [verified, setVerified] = useState(false)
  const [errored, setErrored] = useState(false)

  return (
    <div className="space-y-2">
      {/*
        Hidden input Cloudflare populates. Render BEFORE the widget so the
        element exists in the DOM when turnstile.render() walks the page
        for same-name inputs. No `value` prop — Cloudflare writes directly.
      */}
      <input
        type="hidden"
        name="cf-turnstile-response"
        defaultValue=""
        readOnly
        data-turnstile-gate
      />

      <TurnstileWidget
        onToken={() => {
          setErrored(false)
          setVerified(true)
        }}
        onExpire={() => {
          setVerified(false)
        }}
        onError={() => {
          setErrored(true)
          setVerified(false)
        }}
        className={className}
      />

      {children({ verified, errored })}
    </div>
  )
}